// Lean Value Stream Mapping helpers — duration parsing and PCE rollup.
// Types live in flowModel.ts alongside NewModel so the wire shape stays
// in one place; this file is the tiny computational kernel the canvas
// uses to render metrics and the headline PCE chip.

import type { VsmStep } from './flowModel';

// Parse "30s" / "2m" / "1h" / "1d" / "0" → seconds. Returns 0 for
// unparseable input — the canvas treats absent metrics as zero so a
// step with weird input still renders rather than crashing the strip.
export function parseDurationSeconds(raw: string | number | null | undefined): number {
  if (raw == null) return 0;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  const trimmed = String(raw).trim().toLowerCase();
  if (!trimmed || trimmed === '0') return 0;
  const m = trimmed.match(/^(\d+(?:\.\d+)?)\s*(s|m|h|d)?$/);
  if (!m) return 0;
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n)) return 0;
  switch (m[2]) {
    case 'd': return n * 86400;
    case 'h': return n * 3600;
    case 'm': return n * 60;
    case 's':
    default: return n;
  }
}

// Render a seconds total in the largest unit that keeps the integer/
// one-decimal part readable. Used by the timeline's LT/VA chips so the
// totals come out as "54.1h" rather than "194460s". Picks days for
// anything ≥ 2 days; hours from 1 hour to 2 days; minutes below that.
export function humanizeSeconds(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0';
  if (seconds >= 2 * 86400) return `${trimDecimal(seconds / 86400)}d`;
  if (seconds >= 3600) return `${trimDecimal(seconds / 3600)}h`;
  if (seconds >= 60) return `${trimDecimal(seconds / 60)}m`;
  return `${trimDecimal(seconds)}s`;
}

function trimDecimal(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

// PCE = ΣCT / Σ(CT + WT) × 100, rounded to one decimal. Returns 0 when
// the denominator is 0 — the canvas detects an empty step list before
// calling and renders an em-dash instead of "0%", so this fallback only
// triggers on the pathological all-zeros case.
export function computePce(steps: VsmStep[]): number {
  let ctTotal = 0;
  let leadTotal = 0;
  for (const step of steps) {
    const ct = parseDurationSeconds(step.ct);
    const wt = parseDurationSeconds(step.wt);
    ctTotal += ct;
    leadTotal += ct + wt;
  }
  if (leadTotal === 0) return 0;
  return Math.round((ctTotal / leadTotal) * 1000) / 10;
}
